import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, developedTestimonials, faqs, modules, pains, purchaseUrl, testimonials } from '../data/content';
import { Button } from './ui/Button';
const collapseTransition = {
  duration: 0.22,
  ease: [0.23, 1, 0.32, 1] as const
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-extrabold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-primary'}`} data-id="element-17" data-mp="1no478ia3clrt">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-black leading-tight text-ink md:text-4xl" data-id="element-18" data-mp="2z2zwey6d65mm">
      {children}
    </h2>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-12 md:py-14" data-id="element-19" data-mp="1bodrvpdke6x9">
      <Eyebrow data-id="element-20" data-mp="16q2gha7mfocx">⭐ 4,9/5 · +150 entrenadores</Eyebrow>
      <SectionTitle data-id="element-21" data-mp="16rdgcyxy4byr">
        Lo que dicen los que <span className="text-primary" data-id="element-22" data-mp="12oh73yuaps7h">ya lo están usando</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-23" data-mp="xpw4jsrkmwfv">
        {testimonials.map(item => <article key={item.name} className="relative flex flex-col rounded-2xl border border-line bg-white p-6 shadow-sm" data-id="element-24" data-mp="sqsyc4zuuk6b">
            <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-primary/15" data-id="element-25" data-mp="nyjf17t8wid0" />
            <div className="mb-3 text-sm text-gold" data-id="element-26" data-mp="j218q3i9obyk">⭐⭐⭐⭐⭐</div>
            <p className="mb-5 flex-1 text-base font-semibold italic leading-relaxed text-slate-700" data-id="element-27" data-mp="ezo63nytgu60">
              “{item.text}”
            </p>
            <div className="mt-auto flex items-center gap-3 border-t border-line pt-4" data-id="element-28" data-mp="9zs2hpu174qz">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-29" data-mp="52jidjpboqn3">
                {item.initials}
              </span>
              <div data-id="element-30" data-mp="e0ly2sm5pn5">
                <p className="text-sm font-extrabold text-ink" data-id="element-31" data-mp="acrn1fcgz7d">{item.name}</p>
                <p className="text-xs text-slate-500" data-id="element-32" data-mp="3hmnl8p1loimt">📍 {item.place}</p>
              </div>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Pain() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-33" data-mp="e4vv7g70y8r9">
      <div className="mx-auto max-w-4xl" data-id="element-34" data-mp="j1vun89dcd9e">
        <Eyebrow danger data-id="element-35" data-mp="4eaqdb0rc7zw">😤 La realidad</Eyebrow>
        <SectionTitle data-id="element-36" data-mp="4d9bswkj93df">
          ¿Cuántos programas más vas a{' '}
          <span className="text-primary" data-id="element-37" data-mp="9d42az0ebqxy">diseñar desde cero?</span>
        </SectionTitle>
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-38" data-mp="x2ydzd8zb9gm">
          {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-line bg-canvas p-4" data-id="element-39" data-mp="11tokwux1zkdf">
              <XIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-40" data-mp="2mison7g6n8s7" />
              <span className="text-sm font-semibold text-slate-700 md:text-base" data-id="element-41" data-mp="2hlirt6qgruuc">{pain}</span>
            </div>)}
        </div>
        <p className="mt-8 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-42" data-mp="2w9dm2rk4e79j">
          No es falta de experiencia.{' '}
          <span className="font-black text-primary" data-id="element-43" data-mp="2rd6der0cahnw">Es falta de un SISTEMA.</span>
        </p>
      </div>
    </section>;
}
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-14" data-id="element-44" data-mp="2clxsgv7dtkh3">
      <Eyebrow data-id="element-45" data-mp="2cn8uaph9oi5k">
        <span className="inline-flex items-center gap-2" data-id="element-46" data-mp="2cjkzzo3zzrps">
          <TrophyIcon className="h-4 w-4" data-id="element-47" data-mp="28jj8u8vw2gor" /> Qué incluye el sistema
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-48" data-mp="3o01m20a256lm">
        6 tomos. <span className="text-primary" data-id="element-49" data-mp="3nz9quqkdhavv">Principiante → Élite.</span>
      </SectionTitle>
      <p className="mt-3 text-center font-semibold text-slate-600" data-id="element-50" data-mp="3jyy2p3sswsfn">
        530+ páginas con técnica, periodización y casos reales listos para implementar.
      </p>

      <div className="mt-8 space-y-3" data-id="element-51" data-mp="n53ky3gjbmq7">
        {modules.map((mod, index) => <div key={mod.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary shadow-lg shadow-slate-200' : 'border-line'}`} data-id="element-52" data-mp="s5809q5pibeu" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-53" data-mp="122mhhrzgf487">
              <span className="flex items-center gap-3" data-id="element-54" data-mp="1fu9fpxcyimtm">
                <span className="text-2xl" data-id="element-55" data-mp="1ftra4a4emw91">{mod.icon}</span>
                <span data-id="element-56" data-mp="1kuezkrczj515">
                  <b className="block text-sm font-black uppercase text-ink md:text-base" data-id="element-57" data-mp="1kux1a3if2bgg">
                    {mod.name}
                  </b>
                  <small className="font-bold text-primary" data-id="element-58" data-mp="3aarb5vhsq6qz">{mod.period}</small>
                </span>
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-59" data-mp="35b4xjky22ohn" />
            </button>
            <AnimatePresence initial={false} data-id="element-60" data-mp="2vea2xd4wl88n">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={collapseTransition} data-id="element-61" data-mp="3t0z7nuqzpyy7">
                  <ul className="space-y-2 border-t border-line px-5 py-4 text-sm font-semibold text-slate-600 md:px-6" data-id="element-62" data-mp="3j9cq7go273n0">
                    {mod.content.map(line => <li key={line} className="flex gap-2" data-id="element-63" data-mp="fn4a10ismy8b">
                        <span className="text-primary" data-id="element-64" data-mp="avvy9xtzr0rz">→</span>
                        {line}
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
export function BonusesSection() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-65" data-mp="1vcvgrzhc7z6s">
      <div className="mx-auto max-w-6xl" data-id="element-66" data-mp="20bzn8icvnitf">
        <Eyebrow data-id="element-67" data-mp="20awvy9bn7ecz">
          <span className="inline-flex items-center gap-2" data-id="element-68" data-mp="20aewz4xlim1d">
            <GiftIcon className="h-4 w-4" data-id="element-69" data-mp="1mbf57oesdr0z" /> 6 bonos siempre incluidos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-70" data-mp="1qg4zj8tsqxjs">
          Además te llevás <span className="text-primary" data-id="element-71" data-mp="1cesnv9lo5i3p">6 bonos gratis</span>
        </SectionTitle>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-72" data-mp="1ch5c24i0sop7">
          {bonuses.map(bonus => <article key={bonus.number} className="flex flex-col rounded-2xl border border-line bg-canvas p-5 shadow-sm" data-id="element-73" data-mp="36sttqefmz5da">
              <div className="flex gap-3" data-id="element-74" data-mp="3bremxtwh0os8">
                <span className="text-3xl" data-id="element-75" data-mp="104bch5e5x0bi">{bonus.icon}</span>
                <div data-id="element-76" data-mp="104uk9ta3m8bt">
                  <small className="font-extrabold uppercase tracking-wide text-primary" data-id="element-77" data-mp="v5hzde2sfbbv">
                    Bono {bonus.number}
                  </small>
                  <h3 className="text-base font-black uppercase leading-tight text-ink" data-id="element-78" data-mp="19v5xy7jl3vvw">
                    {bonus.name}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-79" data-mp="1iu18twxkpu5j">
                {bonus.description}
              </p>
              <ul className="my-4 flex-1 space-y-1.5 text-sm font-semibold leading-snug text-slate-600" data-id="element-80" data-mp="1dy2c9m23d5u1">
                {bonus.items.map(item => <li key={item} className="flex gap-2" data-id="element-81" data-mp="1nv71xvv5bjfl">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" data-id="element-82" data-mp="3ukln6y35ncjf" />
                    {item}
                  </li>)}
              </ul>
              <div className="mt-auto flex items-center border-t border-line pt-4 text-sm font-extrabold" data-id="element-83" data-mp="3pn3en8t1uzby">
                <span className="text-slate-400 line-through" data-id="element-84" data-mp="33s33h3cjsl0i">{bonus.value} valor</span>
                <span className="ml-auto rounded-full bg-accent px-3 py-1 text-ink" data-id="element-85" data-mp="38pv5iq6wpnif">GRATIS HOY</span>
              </div>
            </article>)}
        </div>
        <p className="mt-7 text-center text-base font-bold text-slate-600 md:text-lg" data-id="element-86" data-mp="3ihipk614w29v">
          Valor total de bonos: <s className="text-slate-400" data-id="element-87" data-mp="2kuimeig0yqqg">$66.000 ARS</s> →{' '}
          <span className="font-black text-primary" data-id="element-88" data-mp="2psk87b4u545m">incluidos gratis hoy</span>
        </p>
      </div>
    </section>;
}
export function Results() {
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-14" data-id="element-89" data-mp="2uq3mgb1j9gxk">
      <Eyebrow data-id="element-90" data-mp="2ytib1q3jf5mm">❤️ Resultados reales</Eyebrow>
      <SectionTitle data-id="element-91" data-mp="2yr5kwpnmloyh">
        Entrenadores que <span className="text-primary" data-id="element-92" data-mp="a1z0lj7hx60m">revolucionaron su negocio</span>
      </SectionTitle>

      <div className="mt-8 space-y-4" data-id="element-93" data-mp="e2ra8dka5cxf">
        {developedTestimonials.map(item => <article key={item.name} className="rounded-2xl border border-line bg-white p-5 shadow-sm md:flex md:gap-6 md:p-6" data-id="element-94" data-mp="1yl24b17bcndw">
            <div className="flex items-center gap-3 md:w-60 md:shrink-0 md:flex-col md:items-start" data-id="element-95" data-mp="1uk034etu619a">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-96" data-mp="1pr6vnj6zgeti">
                {item.initials}
              </span>
              <div data-id="element-97" data-mp="1fs7ns2u1kn7k">
                <p className="text-sm font-extrabold text-ink" data-id="element-98" data-mp="1avr17d2hqgwl">{item.name}</p>
                <p className="text-xs font-semibold leading-snug text-slate-500" data-id="element-99" data-mp="1axu06mlmfkdg">📍 {item.place}</p>
              </div>
            </div>
            <div className="mt-4 border-t border-line pt-4 md:mt-0 md:flex-1 md:border-l md:border-t-0 md:pl-6 md:pt-0" data-id="element-100" data-mp="11vcgaetfv3tm">
              <div className="flex flex-wrap items-center gap-3" data-id="element-101" data-mp="316lvcuqktksb">
                <span className="text-sm text-gold" data-id="element-102" data-mp="2w93m138nd3js">⭐⭐⭐⭐⭐</span>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-primary" data-id="element-103" data-mp="2w6r2iln8ql4m">
                  {item.result}
                </span>
              </div>
              <p className="mt-3 text-sm font-semibold italic leading-relaxed text-slate-700" data-id="element-104" data-mp="208e8swxp90fa">
                “{item.text}”
              </p>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Guarantee() {
  return <section className="mx-auto max-w-5xl px-4 pb-12 md:pb-14" data-id="element-105" data-mp="1lhypjnoug2jr">
      <div className="flex flex-col items-center gap-5 rounded-3xl border-2 border-primary/20 bg-white p-6 text-center shadow-sm md:flex-row md:p-8 md:text-left" data-id="element-106" data-mp="1hg3o8j6wxp5z">
        <span className="inline-flex shrink-0 rounded-full border-2 border-primary bg-canvas p-4" data-id="element-107" data-mp="1v91lyc6tvtcp">
          <ShieldIcon className="h-9 w-9 text-primary" data-id="element-108" data-mp="1qe5noub009r5" />
        </span>
        <div data-id="element-109" data-mp="12p34vj4moav2">
          <h3 className="text-lg font-black uppercase text-ink md:text-xl" data-id="element-110" data-mp="12ouubxvz9hn1">
            Garantía de 7 días, sin preguntas
          </h3>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-111" data-mp="39f6fiegojst4">
            Abrís los tomos y los usás con tus atletas. Si no es lo que esperabas, escribís a
            compradigitalyaa@gmail.com y te devolvemos el 100%. Sin formularios, sin drama. Tasa de
            devoluciones: <b className="text-primary" data-id="element-112" data-mp="35dcluq4wjp2g">menos del 0,5%</b>.
          </p>
        </div>
      </div>
    </section>;
}
export function FAQSection() {
  const [open, setOpen] = useState(0);
  return <section className="border-t border-line bg-white px-4 py-12 md:py-14" data-id="element-113" data-mp="z4k626o11y8">
      <div className="mx-auto max-w-3xl" data-id="element-114" data-mp="x0jdt42cj26">
        <Eyebrow data-id="element-115" data-mp="3j59b8mf6rkv5">
          <span className="inline-flex items-center gap-2" data-id="element-116" data-mp="3j3ogavoylzwr">
            <HelpCircleIcon className="h-4 w-4" data-id="element-117" data-mp="3o3khyoa6pf95" /> Preguntas frecuentes
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-118" data-mp="27pub59rokcvt">
          Lo que más <span className="text-primary" data-id="element-119" data-mp="27ot0v7ihnuf5">nos preguntan</span>
        </SectionTitle>
        <div className="mt-8 space-y-3" data-id="element-120" data-mp="2coxg6u7nuj3s">
          {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary/50 shadow-lg shadow-slate-200' : 'border-line'}`} data-id="element-121" data-mp="1smsxenmf8o6q" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-3 p-5 text-left text-sm font-extrabold text-ink md:text-base" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-122" data-mp="1jrwe328zlv55">
                {question}
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-123" data-mp="10vd8uo1b0rdv" />
              </button>
              <AnimatePresence initial={false} data-id="element-124" data-mp="2v9xqssy8q8tk">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={collapseTransition} className="border-t border-line px-5 py-4 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-125" data-mp="2qakw5m14nxgf">
                    {answer}
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>

        <div className="mt-9 rounded-2xl bg-canvas p-6 text-center" data-id="element-126" data-mp="2ew9p8wkxq5wh">
          <p className="text-base font-black text-ink md:text-xl" data-id="element-127" data-mp="2jtjdi8wl8biq">
            El próximo ciclo puede ser diferente.
          </p>
          <p className="mt-2 text-sm font-semibold italic text-primary" data-id="element-128" data-mp="2osctc7zckem2">
            “Finalmente un programa que FUNCIONA.” 🏋️
          </p>
          <p className="text-sm font-semibold italic text-primary" data-id="element-129" data-mp="42x59krij404">
            “Finalmente un atleta que PROGRESA porque tiene un sistema.”
          </p>
          <p className="text-sm font-semibold italic text-primary" data-id="element-130" data-mp="8377qng3jca8">
            “Finalmente una comunidad de entrenadores que SABE qué hacer.”
          </p>
          <div className="mt-5" data-id="element-131" data-mp="1oi8lrwyq8rp">
            <Button href={purchaseUrl} className="w-full text-sm md:w-auto md:text-base" data-id="element-132" data-mp="1pbeyrs0ew9b">
              🏋️ QUIERO MI COMBO POWERLIFTING AHORA →
            </Button>
          </div>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500" data-id="element-133" data-mp="bm6ene00r2p6">
            <span className="flex items-center gap-1" data-id="element-134" data-mp="pkmm575pgx8d">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-135" data-mp="knnseppyesap" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1" data-id="element-136" data-mp="zcjxcdwprp73">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-137" data-mp="zb8s09nme9dv" /> Acceso en 5 minutos
            </span>
            <span className="flex items-center gap-1" data-id="element-138" data-mp="uf0k9hbr5igq">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-139" data-mp="18bxbhjvs91y5" /> Pago seguro
            </span>
          </p>
        </div>
      </div>
    </section>;
}